import { useMemo } from "react";
import { Alert, Box, IconButton, Tooltip, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  IconCloudDownload,
  IconEye,
  IconFiles,
  IconCircleCheck,
  IconBan,
  IconMessage,
  IconAlertTriangle
} from "@tabler/icons-react";
import MxDetailSmallCard from "@rf-l1/page/details/MxDetailCardSmall";
import MxTypography from "@rf-l1/MxTypography";
import MxPopover from "@rf-l1/MxPopover";
import { usePropertyDetailPage } from "../../context/PropertyDetailPageContext";
import useUserHasPermission from "@/shared/hooks/use-useris.hook";
import { useLoggedUser } from "@/shared/store/auth";
import useShowFileModal from "@rf-hooks/useShowFileModal";
import useDownloadDocument from "@/src-refactor/modules/shared/hooks/useDowloadFile";
import useModal from "@/src-refactor/modules/shared/components/refactor/base/modal/useModal";
import useAlert from "@/src-refactor/modules/shared/components/refactor/hooks/useAlert";
import { useApproveDocumentMutation } from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import OppositionCommentModalTemplate from "./useDeclineDocumentModal";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { capitalizeName } from "@/src-refactor/modules/shared/components/typography/text-simple/TextSimple";
import useDeclineDocumentModal from "./useDeclineDocumentModal";
import useApproveDocument from "./useApproveDocument";
import usePermissionValidationDocuments from "./usePermissionValidationDocuments";

/**
 * PropertyValidationDocumentsCard
 *
 * Tarjeta para la validación de documentos de proyectos.
 * Permite aprobar, rechazar y visualizar documentos.
 *
 * Reglas de visibilidad:
 * - Solo para propiedades tipo Proyecto (isProject = true)
 * - Visible para Super Admin, Agentes captadores y usuario legacy
 */
const PropertyValidationDocumentsCard = () => {
  const { validations = [], alertsValidation = [] } = usePropertyDetailPage();
  const { declineDocument } = useDeclineDocumentModal();
  const { showFileModal } = useShowFileModal();
  const { downloadDocument } = useDownloadDocument();
  const { handleApproveDocument } = useApproveDocument();
  const { hasPermissionToManage } = usePermissionValidationDocuments();
  const { infoAlert } = useAlert();

  if (!hasPermissionToManage) {
    return null;
  }

  const handleOpenInNewWindow = (url: string) => {
    if (!url) return;
    window.open(url, "_blank");
  };

  const handleSendComment = (documentName: string, documentId: number) => {
    declineDocument(documentName, documentId);
  };

  const showInfoAlert = (message: string) => {
    infoAlert({
      title: "Razón de rechazo",
      message
    });
  };

  return (
    <MxDetailSmallCard title="Documentos validación de proyecto">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Alerts Section */}
        {alertsValidation.length > 0 && (
          <Box>
            {alertsValidation
              .filter(item => item.message !== "")
              .map(alert => (
                <Alert
                  key={alert.id}
                  severity={alert.isExpired ? "error" : "warning"}
                  sx={{ marginBottom: 1 }}
                >
                  <MxTypography
                    variant="overline"
                    color={alert.isExpired ? "error.main" : "warning.main"}
                    fontWeight={500}
                  >
                    {alert.message}
                  </MxTypography>
                </Alert>
              ))}
          </Box>
        )}

        {/* Documents List */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {validations.map(document => (
            <DocumentRow key={document.id}>
              {/* Actions Left */}
              <ActionsContainer>
                <Tooltip title="Descargar documento">
                  <ActionButton
                    size="small"
                    className="blue"
                    onClick={() => downloadDocument(document.url, document.name)}
                  >
                    <IconCloudDownload size={18} />
                  </ActionButton>
                </Tooltip>

                <Tooltip title="Ver documento">
                  <ActionButton
                    size="small"
                    className="blue"
                    onClick={() =>
                      showFileModal({
                        url: document.url,
                        dateMsg: document.dateExpedition
                          ? `Fecha de emisión ${dayjs(
                              document.dateExpedition
                            ).format("DD-MM-YYYY")}`
                          : "-"
                      })
                    }
                  >
                    <IconEye size={18} />
                  </ActionButton>
                </Tooltip>

                <Tooltip title="Abrir documento en otra pestaña">
                  <ActionButton
                    size="small"
                    className="blue"
                    onClick={() => handleOpenInNewWindow(document.url)}
                  >
                    <IconFiles size={18} />
                  </ActionButton>
                </Tooltip>
              </ActionsContainer>

              {/* Document Name */}
              <Box sx={{ flex: 1, minWidth: 0, px: 1 }}>
                <Tooltip title={document.name}>
                  <MxTypography
                    variant="body2"
                    color="primary.main"
                    fontWeight="400"
                    lines={1}
                  >
                    {capitalizeName(document.name, "first")}
                  </MxTypography>
                </Tooltip>
              </Box>

              {/* Status Actions Right */}
              <ActionsContainer>
                {document.message_alert && !document.status.approved && (
                  <Tooltip title={document.message_alert}>
                    <Box>
                      <MxPopover message={document.message_alert ?? ""}>
                        <ActionButton size="small" className="yellow">
                          <IconAlertTriangle size={18} />
                        </ActionButton>
                      </MxPopover>
                    </Box>
                  </Tooltip>
                )}

                {document.status_comment && document.status.rejected && (
                  <ActionButton
                    size="small"
                    className="blue"
                    onClick={() => showInfoAlert(document.status_comment)}
                  >
                    <IconMessage size={18} />
                  </ActionButton>
                )}

                {document.status.pending && hasPermissionToManage && (
                  <>
                    <Tooltip title="Aprobar documento">
                      <ActionButton
                        size="small"
                        className="green"
                        onClick={() => handleApproveDocument(document.id)}
                      >
                        <IconCircleCheck size={18} />
                      </ActionButton>
                    </Tooltip>

                    <Tooltip title="Rechazar documento">
                      <ActionButton
                        size="small"
                        className="red"
                        onClick={() => handleSendComment(document.name, document.id)}
                      >
                        <IconBan size={18} />
                      </ActionButton>
                    </Tooltip>
                  </>
                )}

                {document.status.approved && (
                  <MxTypography
                    variant="caption"
                    color="success.main"
                    fontWeight="500"
                  >
                    Aprobado
                  </MxTypography>
                )}
              </ActionsContainer>
            </DocumentRow>
          ))}
        </Box>
      </Box>
    </MxDetailSmallCard>
  );
};

export default PropertyValidationDocumentsCard;

const DocumentRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.5, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none"
  }
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
  alignItems: "center"
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  "&.blue": {
    color: theme.palette.primary.main
  },
  "&.green": {
    color: theme.palette.success.main
  },
  "&.red": {
    color: theme.palette.error.main
  },
  "&.yellow": {
    color: theme.palette.warning.main
  }
}));
